import type { SongMeaning } from '@/types/album';

export const songs: SongMeaning[] = [
  {
    n: 1,
    title: 'The Grudge',
    duration: '8:36',
    summary:
      'Aprieta sobre una idea muy humana y muy fea: aferrarse al rencor hasta volverlo identidad. Duele porque entiende que soltar no es un gesto amable, es una pelea brutal contra el ego, el miedo a estar equivocado y el apego al daño sufrido.',
    perspective:
      'La voz arranca acusadora y dura, casi como si señalara a una persona obsesionada con controlar, justificar y no perdonar. Después gira hacia algo más amplio y más compasivo: sigue habiendo juicio, pero aparece una salida.',
    voice: 'Segunda persona confrontativa, con tono de advertencia y luego de purga.',
    themes: ['resentimiento como prisión', 'liberación mediante entrega', 'miedo al control'],
    tone: ['acusador', 'catártico', 'ritual'],
    symbols: [
      {
        quote: 'Give away the stone',
        meaning:
          'La piedra es el peso del rencor, algo que hunde, inmoviliza y uno insiste en cargar porque cree que ahí está su razón. Cuando la suelta, la canción imagina una transformación real, no olvido barato.',
      },
      {
        quote: 'Saturn comes back around',
        meaning:
          'Saturno trae tiempo, consecuencia y retorno. Aparece como una fuerza que te devuelve a la misma lección hasta que dejás de defender tu herida y aceptás cambiar.',
      },
    ],
    key_lines: [
      {
        lyric: 'Wear the grudge like a crown',
        explanation:
          'El golpe central. La canción trata el rencor como algo que uno exhibe con orgullo, casi como identidad. Por eso pega tanto: muestra una herida convertida en corona, en moral y en ego.',
      },
      {
        lyric: 'Give away the stone',
        explanation:
          'La línea de liberación que la gente espera. Después de tanta presión, control y peso, acá aparece la única salida real. En vivo cae como exorcismo colectivo.',
      },
      {
        lyric: 'Let go!',
        explanation:
          'El mantra final condensa todo. Ya no hay argumento ni defensa, solo rendición. La canción llega a un punto donde entender menos importa que aflojar.',
      },
    ],
    alternative: {
      label: 'Ciclo de karma y crecimiento',
      description:
        'Lectura más espiritual o astrológica: Saturno marca un ciclo de juicio y maduración. La base está en la repetición de "Saturn comes back around" y en la idea de ser humillado hasta aprender a soltar.',
    },
    live: {
      mindset:
        'Rabia contenida que termina pidiendo limpieza. Entrás apretando los dientes y salís soltando peso.',
      listen_for:
        'El cambio cuando la canción deja la acusación y entra en la liberación de "Give away the stone". Ahí la tensión se abre y el final se vuelve casi ritual.',
      line_to_feel: 'Let go!',
    },
  },
  {
    n: 5,
    title: 'Schism',
    duration: '6:47',
    summary:
      'Duele porque describe una fractura que no nace de una sola traición, sino del desgaste, el ego y la incapacidad de seguir hablándose de verdad. La canción se aferra a una idea insoportable: si alguna vez las piezas encajaron, entonces la pérdida tenía arreglo y también responsabilidad compartida.',
    perspective:
      'La voz habla desde después del quiebre. Mira la relación con dolor, culpa compartida y una necesidad casi obsesiva de entender dónde se perdió el vínculo.',
    voice: 'Primera persona íntima, analítica y herida.',
    themes: [
      'ruptura por falla de comunicación',
      'culpa compartida',
      'belleza dentro del conflicto',
    ],
    tone: ['tenso', 'dolido'],
    symbols: [
      {
        quote: 'I know the pieces fit',
        meaning:
          'Las piezas son los fragmentos de una relación, pero también de una comprensión mutua que antes parecía completa. Cada vez que vuelven, la canción insiste en que la unión existió y por eso la separación se siente como algo casi antinatural.',
      },
      {
        quote: 'Watch the temple topple over',
        meaning:
          'El templo lleva la pelea a un plano más grande. La relación aparece como algo sagrado que el orgullo puede derrumbar por impulso, por necesidad de culpar y ganar la discusión.',
      },
      {
        quote: 'Finding beauty in the dissonance',
        meaning:
          'La disonancia resume la visión más madura del tema. No toda fricción destruye. Parte de amar de verdad está en soportar diferencia, tensión y roce sin convertirlos en ruina.',
      },
    ],
    key_lines: [
      {
        lyric: 'I know the pieces fit',
        explanation:
          'La confesión central. El narrador sabe que hubo una forma de encajar, de entenderse, y por eso la ruptura pesa más. No recuerda una fantasía, recuerda algo real que se perdió.',
      },
      {
        lyric: 'Cold silence has',
        explanation:
          'El golpe más humano de la canción. El problema no es una pelea concreta, es el enfriamiento que va matando la empatía poco a poco. Mucha gente lo siente como la verdad más dura del tema.',
      },
    ],
    alternative: {
      label: 'Fractura interna, no solo de pareja',
      description:
        'Aunque la voz le habla a alguien, varias imágenes (templo, piezas, disonancia) sostienen una lectura paralela: la canción describe también una fractura dentro de uno mismo, entre lo que pensás y lo que sentís.',
    },
    live: {
      mindset:
        'Rabia contenida y tristeza lúcida. La canción pide sentir cómo una relación puede romperse sin que nadie sea del todo el villano.',
      listen_for:
        'El cambio entre la observación fría de las estrofas y la insistencia casi desesperada del final. La repetición convierte una idea racional en una herida.',
      line_to_feel: 'I know the pieces fit',
    },
  },
  {
    n: 9,
    title: 'Lateralus',
    duration: '9:24',
    summary:
      'Gira alrededor de una tensión muy Tool: la mente quiere ordenar el mundo, pero esa obsesión termina secando la intuición y la experiencia viva. La salida no llega con una respuesta clara, llega cuando el narrador acepta el misterio, el azar, el cuerpo y ese movimiento en espiral que mezcla crecimiento, arte y hambre espiritual.',
    perspective:
      'La voz está atrapada entre control y apertura. Quiere sentir más, percibir más, vivir más hondo, pero sabe que su propia mente lo bloquea. El tema entero empuja desde esa pelea interna hacia una rendición activa ante lo desconocido.',
    voice: 'Primera persona intensa, reflexiva y corporal.',
    themes: [
      'control mental vs intuición',
      'expansión de la conciencia',
      'presencia física y espiritual',
    ],
    tone: ['urgente', 'ascendente', 'extático'],
    symbols: [
      {
        quote: 'To swing on the spiral',
        meaning:
          'La espiral junta varias capas a la vez: crecimiento, expansión de conciencia, ritmo, evolución y viaje sin final limpio. No apunta a una meta fija. Apunta a seguir abriéndose.',
      },
      {
        quote: 'Black then white are',
        meaning:
          'Los colores trazan una ampliación de la percepción. El paso de "Black then white" a "Red and yellow" suena a infancia mental, a una visión binaria que después se complica, se enriquece y empieza a captar más dimensiones.',
      },
    ],
    key_lines: [
      {
        lyric: 'Over thinking, over analyzing',
        explanation:
          'La línea que concentra la pelea central. El narrador sabe que pensar demasiado le rompe la unidad interna. No habla de inteligencia, habla de desconexión: la mente se vuelve un filtro tan rígido que apaga el instinto.',
      },
      {
        lyric: 'Spiral out, keep going',
        explanation:
          'La liberación que el público siente en el cuerpo. Después de tanta tensión mental, la canción pide expansión, riesgo y movimiento. Por eso esta frase se vuelve consigna colectiva: mandato y permiso a la vez.',
      },
      {
        lyric: 'How a divinity can',
        explanation:
          'Aterriza toda la ambición mística del tema. La búsqueda no consiste en escapar de lo humano. Quiere tocar algo más alto sin dejar el cuerpo, la tierra y la fragilidad.',
      },
    ],
    alternative: {
      label: 'Apertura espiritual o salto creativo',
      description:
        'Otra lectura la oye como una canción sobre el estado creativo y musical mismo. La obsesión mental corta el flujo, y el giro llega cuando el narrador se entrega al ritmo, al azar y a una expansión casi física de la percepción.',
    },
    live: {
      mindset: 'La canción pide entrega antes que entendimiento. Entrá con hambre de expansión, pero con los pies en la tierra.',
      listen_for:
        'El momento en que la canción deja la autopsia mental y entra en una apertura física, sobre todo desde "Feed my will to feel this moment" hasta el cierre repetido.',
      line_to_feel: 'Spiral out, keep going',
    },
  },
];
